// React
import { Fragment, ReactNode, useCallback, useMemo, useRef } from "react"

// Visx
import { Axis, Orientation, TickRendererProps } from "@visx/axis"
import { curveMonotoneX } from "@visx/curve"
import { localPoint } from "@visx/event"
import { GridRows } from "@visx/grid"
import { Group } from "@visx/group"
import { MarkerCircle } from "@visx/marker"
import { scaleBand, scaleLinear } from "@visx/scale"
import { LinePath } from "@visx/shape"
import { Text } from "@visx/text"
import { useTooltip } from "@visx/tooltip"

// Animations
import { AnimatePresence, motion } from "@/lib/animations"

// Dates
import { DateTime } from "@/lib/dates"

// Context
import { useLang } from "@/context/useLang"
import { cn } from "@/lib/utils"
import { ParentSize } from "../../../visx/components/ParentSize"
import { findLongestArrayInMultiGraphData } from "../../../visx/lib/MultiGraph"
import { isMobile } from "@/lib/device"
import { TooltipAdapter } from "../../../visx/components/Tooltip/TooltipAdapter"
import { MultiGraphTooltipProps } from "./MultiGraphContent"

// Only show the bar data grid rows if there is no line datas.
// We do this to prevent having 2 axes renders, with grid rows, since
// it can be very confusing.
const SHOW_LINE_DATA_GRID_ROWS = false

export type GraphData = {
	x: string
	y: number
}

export type BarGraphData = {
	data: Array<GraphData>
	id: string
	variant?: "primary" | "default"
}

export type LineGraphData = {
	data: Array<GraphData>
	id: string
	variant?: "primary" | "default"
	// Which y-axis to render against. Defaults to "right" (the dedicated line
	// axis); use "left" when the line shares units with the bar series.
	axis?: "left" | "right"
}

type ValueFormatter = (value: string | number) => string | number

export type MultiGraphProps = {
	width: number
	height: number
	margin?: { top: number; right: number; bottom: number; left: number }
	barDatas?: Array<BarGraphData>
	lineDatas?: Array<LineGraphData>
	lineYDomain?: [number, number]
	// Hide the left Y-axis. Useful for line-only graphs where the bar/left
	// axis would otherwise render an empty scale.
	hideLeftAxis?: boolean
	// Render multiple bar series side-by-side within each x band instead of
	// overlaying them. Defaults to false to preserve the existing overlay
	// behavior used by production/expected-production graphs.
	groupedBars?: boolean
	xTickComponent?: (tickRendererProps: TickRendererProps) => ReactNode
	yTickComponent?: (tickRendererProps: TickRendererProps) => ReactNode
	rightYTickComponent?: (tickRendererProps: TickRendererProps) => ReactNode
	prepareTooltipValues?:
		| ((x: TooltipData["x"]) => MultiGraphTooltipProps["values"])
		| null
	prepareTooltipLabel?:
		| ((x: TooltipData["x"]) => MultiGraphTooltipProps["label"])
		| null
}

export function MultiGraphContainer(
	props: Omit<MultiGraphProps, "width" | "height">,
) {
	return (
		<ParentSize>
			{({ width, height }) => {
				if (width < 10) return null
				return <MultiGraph {...props} width={width} height={height} />
			}}
		</ParentSize>
	)
}

MultiGraphContainer.defaultMargin = { top: 25, bottom: 40, left: 66, right: 25 }
MultiGraphContainer.barVariantColourMap = {
	primary: "#FFD900",
	default: "#4e5155",
}
MultiGraphContainer.lineVariantColourMap = {
	primary: "#149AE5",
	default: "#000",
}

MultiGraphContainer.buildBarDataTooltip = (
	d: BarGraphData,
	x: string,
	valueFormatter: ValueFormatter,
) => {
	const item = d.data.find((node) => node.x === x)

	return {
		value: valueFormatter(item ? getY(item).toFixed(2) : 0),
		id: d.id,
		variant: d.variant,
		colour:
			d.variant === "primary"
				? MultiGraphContainer.barVariantColourMap.primary
				: MultiGraphContainer.barVariantColourMap.default,
	}
}

MultiGraphContainer.buildLineDataTooltip = (
	d: LineGraphData,
	x: string,
	valueFormatter: ValueFormatter = (x) => x,
) => {
	const item = d.data.find((node) => node.x === x)
	const value = item ? getY(item).toFixed(2) : null

	return {
		value: value !== null ? valueFormatter(value) : null,
		id: d.id,
		variant: d.variant,
		colour:
			d.variant === "primary"
				? MultiGraphContainer.lineVariantColourMap.primary
				: MultiGraphContainer.lineVariantColourMap.default,
	}
}

export { MultiGraphContainer as MultiGraph }

const brandGray = "#4e5155"
const grayLight = "#C7C8CC"

const getX = (d: GraphData) => d.x
const getY = (d: GraphData) => Number(d.y)

interface TooltipData {
	x: GraphData["x"]
	topY: number
	centerX: number
}

function MultiGraph({
	width,
	barDatas: barDatasFromProps = [],
	lineDatas: lineDatasFromProps = [],
	lineYDomain,
	hideLeftAxis = false,
	groupedBars = false,
	height,
	margin = MultiGraphContainer.defaultMargin,
	xTickComponent,
	yTickComponent = DefaultYTickComponent,
	rightYTickComponent,
	prepareTooltipValues,
	prepareTooltipLabel,
}: MultiGraphProps) {
	const { formatDate, formatNumber } = useLang()

	// flatten all datas into one array for picking
	const barDatas = useMemo(() => {
		return barDatasFromProps.length > 1
			? barDatasFromProps[0].data.map((item, i) => ({
					...item,

					// the idea here is to get the maximum Y value
					// to help with the band and linear scales below
					// otherwise the graph doesn't know the highest y value
					// find the largest y by using Math.max
					// we can make a new array with all the items values
					y: Math.max(...barDatasFromProps.map((d) => d.data[i].y)),
				}))
			: (barDatasFromProps[0]?.data ?? [])
	}, [barDatasFromProps])

	// Only right-axis lines drive xScaleLines/yScaleLines. Left-axis lines
	// share the bar series' scales, so they're handled by `yScale` below.
	const rightAxisLineDatas = useMemo(
		() => lineDatasFromProps.filter((d) => (d.axis ?? "right") === "right"),
		[lineDatasFromProps],
	)
	const leftAxisLineDatas = useMemo(
		() => lineDatasFromProps.filter((d) => d.axis === "left"),
		[lineDatasFromProps],
	)

	// flatten all datas into one array for picking
	const lineDatas = useMemo(() => {
		if (rightAxisLineDatas.length > 1) {
			const longestData =
				findLongestArrayInMultiGraphData(rightAxisLineDatas)
			return longestData.data.map((item, i) => {
				// the idea here is to get the maximum Y value
				// to help with the band and linear scales below
				// otherwise the graph doesn't know the highest y value
				return Object.assign({}, item, {
					// find the largest y by using Math.max
					// we can make a new array with all the items values
					y: Math.max(
						...rightAxisLineDatas.map((d) => d.data[i]?.y ?? 0),
					),
				})
			})
		}
		return rightAxisLineDatas[0]?.data ?? []
	}, [rightAxisLineDatas])

	// bounds
	const innerWidth = width - margin.left - margin.right
	const innerHeight = height - margin.top - margin.bottom

	// scales
	const xScale = useMemo(() => {
		// Fall back to line x values when there are no bars so the
		// bottom x-axis still has a domain to render ticks against.
		const xSource =
			barDatas.length > 0
				? barDatas.map(getX)
				: (leftAxisLineDatas[0]?.data ?? lineDatas).map(getX)
		return scaleBand<string>({
			range: [0, innerWidth],
			round: true,
			domain: xSource,
			padding: 0.4,
		})
	}, [innerWidth, barDatas, leftAxisLineDatas, lineDatas])
	const yScale = useMemo(() => {
		// When there are no bars, derive the y domain from left-axis lines
		// so they have a sensible scale to render against.
		const ySource =
			barDatas.length > 0
				? barDatas.map(getY)
				: leftAxisLineDatas.flatMap((d) => d.data.map(getY))
		return scaleLinear<number>({
			range: [innerHeight, 0],
			round: true,
			domain: [0, Math.max(0, ...ySource)],
		})
	}, [innerHeight, barDatas, leftAxisLineDatas])

	const xScaleLines = useMemo(
		() =>
			scaleBand<string>({
				range: [0, innerWidth],
				round: true,
				domain: lineDatas.map(getX),
				padding: 0.4,
			}),
		[innerWidth, lineDatas],
	)
	const yScaleLines = useMemo(
		() =>
			scaleLinear<number>({
				range: [innerHeight, 0],
				round: true,
				domain: lineYDomain ?? [0, Math.max(...lineDatas.map(getY))],
			}),
		[innerHeight, lineDatas, lineYDomain],
	)

	const {
		tooltipData,
		tooltipLeft = 0,
		tooltipTop,
		showTooltip: showTooltipRaw,
		hideTooltip,
		tooltipOpen,
	} = useTooltip<TooltipData>()

	const showTooltip = useCallback(
		(args: Parameters<typeof showTooltipRaw>[0]) => showTooltipRaw(args),
		[showTooltipRaw],
	)

	const barWidth = xScale.bandwidth()

	const preparedTooltipData = useMemo(() => {
		if (!tooltipOpen || !tooltipData?.x) return null

		return {
			label: prepareTooltipLabel
				? prepareTooltipLabel(tooltipData.x)
				: formatDate(DateTime.fromISO(tooltipData.x), {
						variant: "pretty",
					}),
			values: prepareTooltipValues
				? prepareTooltipValues(tooltipData.x)
				: [
						...Array(barDatasFromProps.length)
							.fill(true)
							.map((_, index) => {
								return MultiGraphContainer.buildBarDataTooltip(
									barDatasFromProps[index],
									tooltipData.x,
									(value) => {
										return formatNumber(
											typeof value === "string"
												? parseFloat(value)
												: value,
										)
									},
								)
							})
							// keep primary variants on top of tooltip
							.sort((a) => {
								if (a.variant === "primary") return -1
								return 1
							}),
						...Array(lineDatasFromProps.length)
							.fill(true)
							.map((_, index) => {
								return MultiGraphContainer.buildLineDataTooltip(
									lineDatasFromProps[index],
									tooltipData.x,
								)
							})
							// keep primary variants on top of tooltip
							.sort((a) => {
								if (a.variant === "primary") return -1
								return 1
							}),
					],
		}
	}, [
		barDatasFromProps,
		formatDate,
		lineDatasFromProps,
		prepareTooltipLabel,
		formatNumber,
		prepareTooltipValues,
		tooltipData?.x,
		tooltipOpen,
	])

	const timeout = useRef<NodeJS.Timeout>(null!)
	const gridRowsWidth = width - margin.left - margin.right
	const hasLineDatas = lineDatas.length > 0

	return (
		<div className="relative">
			<svg width={width} height={height}>
				{/**
				 * Only show the bar data grid rows if there is no line datas.
				 * We do this to prevent having 2 axes renders, with grid rows, since
				 * it can be very confusing.
				 */}
				{useMemo(
					() =>
						!hasLineDatas ? (
							<GridRows
								scale={yScale}
								width={gridRowsWidth}
								height={height - margin.top}
								numTicks={3}
								stroke={grayLight}
								strokeDasharray={"10,2"}
								opacity={0.75}
								top={margin.top}
								left={margin.left}
							/>
						) : null,
					[
						gridRowsWidth,
						hasLineDatas,
						height,
						margin.left,
						margin.top,
						yScale,
					],
				)}
				{SHOW_LINE_DATA_GRID_ROWS ? (
					<GridRows
						scale={yScaleLines}
						width={gridRowsWidth}
						height={height - margin.top}
						numTicks={3}
						stroke={grayLight}
						strokeDasharray={"10,2"}
						opacity={0.75}
						top={margin.top}
						left={margin.left}
					/>
				) : null}
				{/**
				 * This one big group is doing a lot:
				 * - It includes and renders all the bar graph datas
				 * - It includes and renders all the line graph datas
				 * - It includes and renders the hover/interactive part
				 */}
				<Group>
					{/**
					 * These are all the bar datas.
					 */}
					{useMemo(() => {
						// In grouped mode, each series gets its own slot within
						// the x band so bars render side-by-side. Keep the
						// original draw order (primary on top of default) when
						// overlaying, but preserve the input order when grouping
						// so series stay in the same slot regardless of variant.
						const seriesToRender = groupedBars
							? barDatasFromProps
							: [...barDatasFromProps].sort((a) =>
									a.variant === "primary" ? 1 : -1,
								)
						const seriesCount = Math.max(
							1,
							barDatasFromProps.length,
						)
						const slotWidth = groupedBars
							? barWidth / seriesCount
							: barWidth
						return seriesToRender.map(({ data, id, variant }) => {
							const seriesIndex = barDatasFromProps.findIndex(
								(s) => s.id === id,
							)
							return (
								<Fragment key={id}>
									{data.map((d) => {
										const valueX = getX(d)
										const valueY = getY(d)
										const x = xScale(valueX) ?? 0
										const y =
											Math.max(yScale(valueY), 0) || 0

										const barHeight = innerHeight - y
										const barX = groupedBars
											? x +
												margin.left +
												seriesIndex * slotWidth
											: x + margin.left
										const barY =
											innerHeight - barHeight + margin.top

										return (
											<rect
												key={`bar-${id}-${valueX}`}
												className={cn(
													"visx-bar",
													// hover states for bar graph
													variant === "primary"
														? tooltipData?.x === d.x
															? "fill-primary-600"
															: "fill-primary-500-transparent"
														: tooltipData?.x === d.x
															? "fill-gray-400"
															: "fill-gray-300-transparent",
												)}
												x={barX}
												y={barY}
												width={slotWidth}
												height={barHeight}
											/>
										)
									})}
								</Fragment>
							)
						})
					}, [
						barWidth,
						barDatasFromProps,
						groupedBars,
						innerHeight,
						margin.left,
						margin.top,
						tooltipData?.x,
						xScale,
						yScale,
					])}
					{/**
					 * These are all the line datas.
					 */}
					{useMemo(
						() =>
							lineDatasFromProps
								// keep primary variants on top of tooltip
								.sort((a) => {
									if (a.variant === "primary") return -1
									return 1
								})
								.map(({ data, id, variant, axis }) => {
									const useLeftAxis = axis === "left"
									const xS = useLeftAxis
										? xScale
										: xScaleLines
									const yS = useLeftAxis
										? yScale
										: yScaleLines
									return (
										<LinePath
											key={id}
											curve={curveMonotoneX}
											data={data}
											x={(d) =>
												(xS(getX(d)) ?? 0) +
												margin.left +
												barWidth / 2
											}
											y={(d) =>
												(yS(getY(d)) ?? 0) + margin.top
											}
											stroke={
												variant === "primary"
													? MultiGraphContainer
															.lineVariantColourMap
															.primary
													: MultiGraphContainer
															.lineVariantColourMap
															.default
											}
											strokeWidth={2}
											strokeDasharray={
												variant === "primary"
													? ""
													: "6,9"
											}
											shapeRendering="geometricPrecision"
											markerMid={`url(#marker-circle-${variant})`}
											markerStart={`url(#marker-circle-${variant})`}
											markerEnd={`url(#marker-circle-${variant})`}
											clipPath="url(#line-mask)"
										/>
									)
								}),
						[
							barWidth,
							lineDatasFromProps,
							margin.left,
							margin.top,
							xScale,
							xScaleLines,
							yScale,
							yScaleLines,
						],
					)}
					{/**
					 * This is the mask for the line
					 */}
					{useMemo(
						() => (
							<clipPath id="line-mask">
								<motion.rect
									x={0}
									y={0}
									height={innerHeight + margin.top}
									fill="white"
									initial={{ width: 0 }}
									animate={{
										width: innerWidth + margin.left,
									}}
									transition={{
										duration: 1.75,
										delay: 0.75,
									}}
								/>
							</clipPath>
						),
						[innerHeight, innerWidth, margin.left, margin.top],
					)}
					{/**
					 * These are svg markers that the line data
					 * can reference.
					 */}
					{useMemo(() => {
						const variants = Object.entries(
							MultiGraphContainer.lineVariantColourMap,
						)
						return (
							<>
								{variants.map(([key, value]) => {
									return (
										<MarkerCircle
											key={key}
											id={`marker-circle-${key}`}
											fill={value}
											size={2}
											refX={2}
										/>
									)
								})}
							</>
						)
					}, [])}
					{/**
					 * These `rects` are used to highlight the selected bar
					 * So the only render transparent blocks with which the user
					 * can hover over with mouse or touch.
					 *
					 * When there are no bars, we fall back to the first line series
					 * so line-only graphs still get a hover hit-area per x value.
					 */}
					{useMemo(() => {
						const hoverSeries =
							barDatasFromProps[0]?.data ??
							lineDatasFromProps[0]?.data ??
							[]
						return (
							<>
								{Array(hoverSeries.length)
									.fill(true)
									.map((_, index) => {
										const d = hoverSeries[index]
										const x = getX(d)
										const value = getY(d)
										const y = yScale(value) ?? 0

										const barX =
											(xScale(x) ?? 0) + margin.left
										const barY = 0 + margin.top

										const left =
											barX + barWidth + margin.left

										const nextTooltipData: TooltipData = {
											x: d.x,
											topY: y + margin.top,
											centerX: 0, //left - barWidth + 2,
										}

										return (
											<rect
												x={barX}
												y={barY}
												key={index}
												className={cn(
													"visx-bar",
													"fill-transparent",
												)}
												width={barWidth}
												height={innerHeight}
												onTouchStart={(event) => {
													const y =
														localPoint(event)?.y ??
														barY
													clearTimeout(
														timeout.current,
													)

													showTooltip({
														tooltipData:
															nextTooltipData,
														tooltipTop: y,
														tooltipLeft: left,
													})
												}}
												onTouchMove={(event) => {
													const y =
														localPoint(event)?.y ??
														barY
													clearTimeout(
														timeout.current,
													)

													showTooltip({
														tooltipData:
															nextTooltipData,
														tooltipTop: y,
														tooltipLeft: left,
													})
												}}
												onMouseMove={(event) => {
													const y =
														localPoint(event)?.y ??
														barY
													clearTimeout(
														timeout.current,
													)

													showTooltip({
														tooltipData:
															nextTooltipData,
														tooltipTop: y,
														tooltipLeft: left,
													})
												}}
												onMouseLeave={() => {
													timeout.current =
														setTimeout(
															hideTooltip,
															1000,
														)
												}}
											/>
										)
									})}
							</>
						)
					}, [
						barDatasFromProps,
						lineDatasFromProps,
						barWidth,
						hideTooltip,
						innerHeight,
						margin.left,
						margin.top,
						showTooltip,
						xScale,
						yScale,
					])}
				</Group>
				<Axis
					key="y"
					tickComponent={yTickComponent}
					tickLabelProps={() => ({
						fill: "black",
						fontSize: 16,
						fontFamily: "Static",
						fontWeight: 700,
						textAnchor: "middle",
					})}
					top={innerHeight + margin.top - 1}
					left={margin.left}
					orientation={Orientation.bottom}
					scale={xScale}
					stroke="black"
					hideTicks
					numTicks={Math.max(4, Math.floor(innerWidth / 60))}
				/>
				{hideLeftAxis ? null : (
					<Axis
						key="bar-data-y"
						orientation={Orientation.left}
						scale={yScale}
						top={margin.top + 5}
						left={margin.left - 10}
						hideTicks
						numTicks={5}
						hideAxisLine
						tickLabelProps={() => ({
							fontSize: 20,
							color: brandGray,
							fontFamily: "Static",
							fontWeight: 700,
							opacity: 0.5,
							textAnchor: "middle",
						})}
						// Stringify the raw tick value so custom tickComponents
						// receive the actual number (visx's default d3 formatter
						// can apply SI prefixes like "1M" which break parsing).
						tickFormat={(v) => String(v)}
						tickComponent={xTickComponent}
					/>
				)}
				{lineDatas?.length > 0 ? (
					<Axis
						key="line-data-x"
						orientation={Orientation.right}
						scale={yScaleLines}
						top={margin.top + 5}
						left={innerWidth + margin.right + 15}
						hideTicks
						numTicks={5}
						hideAxisLine
						tickLabelProps={() => ({
							fontSize: 20,
							color: brandGray,
							fontFamily: "Static",
							fontWeight: 700,
							opacity: 0.5,
							textAnchor: "middle",
						})}
						tickComponent={rightYTickComponent ?? yTickComponent}
					/>
				) : null}
			</svg>
			<AnimatePresence>
				<>
					{preparedTooltipData && (
						<TooltipAdapter
							left={
								isMobile()
									? undefined
									: tooltipLeft - margin.left
							}
							top={isMobile() ? 0 : tooltipTop}
							tooltipData={preparedTooltipData}
						/>
					)}
				</>
			</AnimatePresence>
		</div>
	)
}

function DefaultYTickComponent({
	formattedValue,
	...props
}: TickRendererProps) {
	return <Text {...props}>{formattedValue}</Text>
}
